import { useEffect, useRef } from 'react'
import confetti from 'canvas-confetti'

const COLORS = ['#6fbf4a', '#f0c040', '#e87a9a', '#6ec6e8']
const RAINBOW = ['#ff5c6c', '#ff9f43', '#ffd24a', '#5dff8a', '#2ee6ff', '#7c6cff', '#ff4d8d']

/** 全局：彩虹丝带光标拖尾 + 小马 GIF 光标 + 点击彩粒（尊重 reduced-motion） */
export default function GameFx() {
  const glowRef = useRef<HTMLDivElement | null>(null)
  const starRef = useRef<HTMLDivElement | null>(null)
  const ribbonRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const fine = window.matchMedia('(pointer: fine)').matches
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const glow = glowRef.current
    const cv = ribbonRef.current

    let raf = 0
    let fadeRaf = 0
    let x = -120
    let y = -120
    let tx = x
    let ty = y
    const points: Array<{ x: number; y: number }> = []
    let ribbonAlpha = 0
    let lastMoveAt = 0
    let idleRaf = 0

    const resizeRibbon = () => {
      if (!cv) return
      cv.width = Math.floor(window.innerWidth * devicePixelRatio)
      cv.height = Math.floor(window.innerHeight * devicePixelRatio)
      cv.style.width = `${window.innerWidth}px`
      cv.style.height = `${window.innerHeight}px`
    }
    resizeRibbon()
    window.addEventListener('resize', resizeRibbon)

    // 彩虹丝带：沿轨迹方向的多条平行色带（固定色序），锚点在小马马尾后下方
    const TAIL_OFFSET_X = 30
    const TAIL_OFFSET_Y = 34
    const BAND_W = 3.2
    const drawRibbon = () => {
      const ctx = cv?.getContext('2d')
      if (!cv || !ctx) return
      ctx.clearRect(0, 0, cv.width, cv.height)
      if (points.length < 2) return
      const dpr = devicePixelRatio || 1
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i]
        const p1 = points[i + 1]
        const dx = p1.x - p0.x
        const dy = p1.y - p0.y
        const len = Math.hypot(dx, dy) || 1
        // perpendicular unit vector
        const nx = -dy / len
        const ny = dx / len
        const t = i / Math.max(1, points.length - 1)
        const widthScale = 0.45 + t * 0.85
        ctx.globalAlpha = 0.18 + t * 0.7

        for (let b = 0; b < RAINBOW.length; b++) {
          const off = (b - (RAINBOW.length - 1) / 2) * BAND_W * widthScale
          ctx.beginPath()
          ctx.lineWidth = BAND_W * widthScale * dpr
          ctx.strokeStyle = RAINBOW[b]
          ctx.moveTo((p0.x + nx * off) * dpr, (p0.y + ny * off) * dpr)
          ctx.lineTo((p1.x + nx * off) * dpr, (p1.y + ny * off) * dpr)
          ctx.stroke()
        }
      }
      ctx.globalAlpha = 1
      ribbonAlpha = 1
    }

    const fadeLoop = () => {
      const ctx = cv?.getContext('2d')
      if (cv && ctx && ribbonAlpha > 0.01) {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.fillStyle = 'rgba(0,0,0,0.14)'
        ctx.fillRect(0, 0, cv.width, cv.height)
        ctx.globalCompositeOperation = 'source-over'
        ribbonAlpha *= 0.95
      }
      const idleClear = () => {
      if (lastMoveAt && Date.now() - lastMoveAt > 140) {
        if (points.length) points.length = 0
        ribbonAlpha = 0
        lastMoveAt = 0
      }
      idleRaf = requestAnimationFrame(idleClear)
    }
    idleRaf = requestAnimationFrame(idleClear)
    fadeRaf = requestAnimationFrame(fadeLoop)
    }
    const idleClear = () => {
      if (lastMoveAt && Date.now() - lastMoveAt > 140) {
        if (points.length) points.length = 0
        ribbonAlpha = 0
        lastMoveAt = 0
      }
      idleRaf = requestAnimationFrame(idleClear)
    }
    idleRaf = requestAnimationFrame(idleClear)
    fadeRaf = requestAnimationFrame(fadeLoop)

    const move = (e: PointerEvent) => {
      tx = e.clientX
      ty = e.clientY
      if (reduce || !fine) return
      if (starRef.current) document.body.classList.add('has-star-cursor')
      if (!raf) {
        raf = requestAnimationFrame(() => {
          x += (tx - x) * 0.3
          y += (ty - y) * 0.3
          if (glow) glow.style.transform = `translate3d(${x}px, ${y}px, 0)`
          if (starRef.current) {
            starRef.current.style.transform = `translate3d(${tx}px, ${ty}px, 0)`
          }
          const nx2 = tx + TAIL_OFFSET_X
          const ny2 = ty + TAIL_OFFSET_Y
          const last = points[points.length - 1]
          if (last) {
            const dist = Math.hypot(nx2 - last.x, ny2 - last.y)
            // 停顿/跳跃后不要跨断点连线
            if (dist > 60) points.length = 0
            else if (dist < 1.5) {
              raf = 0
              return
            }
          }
          points.push({ x: nx2, y: ny2 })
          if (points.length > 30) points.shift()
          drawRibbon()
          lastMoveAt = Date.now()
          raf = 0
        })
      }
    }

    const click = (e: PointerEvent) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('.btn') as HTMLElement | null
      const isCta = Boolean(btn?.closest('.hero-actions'))
      if (btn && !reduce) {
        btn.classList.remove('shine', 'bursting')
        void btn.offsetWidth
        btn.classList.add('shine', 'bursting')
        window.setTimeout(() => btn.classList.remove('shine', 'bursting'), 600)
      }
      if (reduce) return
      confetti({
        particleCount: isCta ? (fine ? 70 : 32) : fine ? 36 : 18,
        spread: isCta ? 110 : 360,
        startVelocity: isCta ? 50 : 28,
        gravity: 0.9,
        decay: 0.88,
        scalar: isCta ? 1.1 : 0.85,
        ticks: isCta ? 140 : 120,
        origin: {
          x: e.clientX / window.innerWidth,
          y: e.clientY / window.innerHeight,
        },
        colors: COLORS,
        disableForReducedMotion: true,
      })
    }

    const btns = Array.from(document.querySelectorAll<HTMLElement>('.btn'))
    const onBtnEnter = (ev: Event) => {
      const el = ev.currentTarget as HTMLElement
      if (reduce) return
      el.classList.add('hover-pop')
      window.setTimeout(() => el.classList.remove('hover-pop'), 500)
      if (!fine) return
      const r = el.getBoundingClientRect()
      confetti({
        particleCount: 14,
        spread: 50,
        startVelocity: 18,
        gravity: 0.6,
        decay: 0.92,
        scalar: 0.7,
        ticks: 60,
        origin: {
          x: (r.left + r.width / 2) / window.innerWidth,
          y: (r.top + r.height / 2) / window.innerHeight,
        },
        colors: COLORS,
        disableForReducedMotion: true,
      })
    }
    const onBtnLeave = (ev: Event) => {
      ;(ev.currentTarget as HTMLElement).classList.remove('hover-pop')
    }
    btns.forEach((b) => {
      b.addEventListener('pointerenter', onBtnEnter)
      b.addEventListener('pointerleave', onBtnLeave)
    })

    window.addEventListener('pointermove', move, { passive: true })
    window.addEventListener('pointerdown', click, { passive: true })

    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerdown', click)
      window.removeEventListener('resize', resizeRibbon)
      btns.forEach((b) => {
        b.removeEventListener('pointerenter', onBtnEnter)
        b.removeEventListener('pointerleave', onBtnLeave)
      })
      document.body.classList.remove('has-star-cursor')
      if (raf) cancelAnimationFrame(raf)
      if (fadeRaf) cancelAnimationFrame(fadeRaf)
      if (idleRaf) cancelAnimationFrame(idleRaf)
    }
  }, [])

  return (
    <>
      <div ref={glowRef} className="pointer-glow" aria-hidden />
      <canvas ref={ribbonRef} className="rainbow-ribbon" aria-hidden />
      <div ref={starRef} className="star-cursor" aria-hidden>
        <img src="/unicorn-cursor-fast.gif" alt="" draggable={false} />
      </div>
    </>
  )
}

/** 生成成功彩带（confetti-burst 40-60 粒） */
export const celebrate = celebrateBurst

export function celebrateBurst() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  confetti({
    particleCount: 55,
    spread: 75,
    startVelocity: 38,
    gravity: 1,
    decay: 0.9,
    origin: { y: 0.35 },
    colors: COLORS,
    disableForReducedMotion: true,
  })
}
